import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { tapAllResponses, TreetopPropertiesProvider } from 'shared/util-common';

import { LicensedFeature, LicenseDto } from '@treetop/shared/model';

import { MessagesService } from '../infrastructure/messages.service';

export type LicensedFeatures = {
  publicLinks: boolean;
  guidelineEditor: boolean;
  statistics: boolean;
  hub: boolean;
  pdfExport: boolean;
  publicReviewLinks: boolean;
  workflow: boolean;
};

const defaultLicenses: LicensedFeatures = {
  publicLinks: false,
  guidelineEditor: false,
  statistics: false,
  hub: false,
  pdfExport: false,
  publicReviewLinks: false,
  workflow: false,
};

export
@Injectable({
  providedIn: 'root',
})
class LicenseService {
  private http = inject(HttpClient);
  private messages = inject(MessagesService);

  private baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  private _licensedFeatures = signal<LicensedFeatures | undefined>({
    publicLinks: false,
    guidelineEditor: false,
    statistics: false,
    hub: false,
    pdfExport: false,
    publicReviewLinks: false,
    workflow: false,
  });
  public licensedFeatures = this._licensedFeatures.asReadonly();

  // Whether the license request has completed (successfully or not); until then licensedFeatures holds the defaults
  private _loaded = signal(false);
  public loaded = this._loaded.asReadonly();

  public loadLicense = rxMethod<void>((trigger$) =>
    trigger$.pipe(
      switchMap(() => this.http.get<LicenseDto>(`${this.baseUrl}/organisations/current/license`)),
      tapAllResponses<LicenseDto>(
        (license) => {
          const licensedFeatures = {
            publicLinks: license?.licensedFeatures?.includes(LicensedFeature.PublicLinks) ?? defaultLicenses.publicLinks,
            guidelineEditor: license?.licensedFeatures?.includes(LicensedFeature.GuidelineEditor) ?? defaultLicenses.guidelineEditor,
            statistics: license?.licensedFeatures?.includes(LicensedFeature.Statistics) ?? defaultLicenses.statistics,
            hub: license?.licensedFeatures?.includes(LicensedFeature.Hub) ?? defaultLicenses.hub,
            pdfExport: license?.licensedFeatures?.includes(LicensedFeature.PdfExport) ?? defaultLicenses.pdfExport,
            publicReviewLinks: license?.licensedFeatures?.includes(LicensedFeature.PublicReviewLinks) ?? defaultLicenses.publicReviewLinks,
            workflow: license?.licensedFeatures?.includes(LicensedFeature.Workflow) ?? defaultLicenses.workflow,
          };

          this._licensedFeatures.set(licensedFeatures);
          this._loaded.set(true);
        },
        (error) => {
          this._loaded.set(true);
          this.messages.showError('CouldNotLoadLicense', error);
        },
      ),
    ),
  );
}
